import { entityType as et, roles } from '../../constants';
import { UserDTO } from './dto/UserDTO';

const hasRole = (user: UserDTO | undefined, role: string) => {
  return user?.authorities?.some(authority => authority.authority === role) ?? false;
};

export const isAdmin = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_ADMIN);
export const isFGSO = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_FGSO);
export const isViewer = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_VIW);
export const isChemicalSafetyExpert = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_CSE);
export const isEnvironmentalExpert = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_ENV);
export const isFireBrigade = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_FBE);
export const isMedicalService = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_MED);
export const isTSO = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_TSO);
export const isDSO = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_DSO);
export const isGlobalViewer = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_VIW_GLOBAL);
export const isEditor = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_EDT);
export const isProductEditor = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_CP_EDT);
export const isHseDeveloper = (user: UserDTO | undefined) => hasRole(user, roles.ROLE_HSE_DEV);

/**
 * PUB role is given to all users due to gateway restriction, a user is considered public only if he has no other role though
 * @param user
 * @returns {*|boolean}
 */
export const isPublic = (user: UserDTO | undefined) => {
  return (
    user &&
    user.authorities &&
    user.authorities.length === 1 &&
    user.authorities.some(authority => authority.authority === roles.ROLE_PUB)
  );
};

export const isInventoryOrChemProductCanCreate = (
  user: UserDTO | undefined,
  entityTypeParam: string
) => {
  return (
    isAdmin(user) ||
    isHseDeveloper(user) ||
    isChemicalSafetyExpert(user) ||
    isEditor(user) ||
    (entityTypeParam === et.product && isProductEditor(user))
  );
};

export const isActivityCanCreate = (user: UserDTO | undefined, entityTypeParam: string) => {
  return (
    (user && isAdmin(user)) || isHseDeveloper(user) || isFGSO(user) || isChemicalSafetyExpert(user)
  );
};

export const isCanViewActivity = (user: UserDTO | undefined, entityTypeParam: string) => {
  return (
      (user && isAdmin(user)) || isHseDeveloper(user) || isFGSO(user) || isChemicalSafetyExpert(user) || isDSO(user) || isTSO(user)
  );
};

export const isInContacts = (entityResponsible: any, user: UserDTO) => {
  return entityResponsible.includes(user.personId);
};

export const isCanModifyAsCreator = (
  entityTypeParam: string,
  user: UserDTO,
  entityResponsible: any,
  creatorUserId?: number | null,
  department?: string
) => {
  if (isAdmin(user) || isChemicalSafetyExpert(user)) return true;
  //const department = orgUnit ? orgUnit.split('-')[0] : null; CERES-142
  const { inventory, batchUpdate, batchUpdateSelected, product, activity } = et;
  if ([inventory, batchUpdate, batchUpdateSelected].includes(entityTypeParam)) {
    entityResponsible = entityResponsible ?? [];

    return (
      (isEditor(user) && user.personId == creatorUserId) ||
      isInContacts(entityResponsible, user) ||
      (department && isDSO(user) && user.actualDepartment === department) ||
      // || String(user.department) === String(department) CERES-142
      isAdmin(user) ||
      isHseDeveloper(user) ||
      isChemicalSafetyExpert(user)
    );
  } else if (product === entityTypeParam) {
    return (
      (isEditor(user) && user.personId == creatorUserId) ||
      isAdmin(user) ||
      isProductEditor(user) ||
      isHseDeveloper(user) ||
      isChemicalSafetyExpert(user)
    );
  } else if (activity == entityTypeParam) {
    return isActivityCanCreate(user, activity);
  }
};

export const isRowDisabled = (user: UserDTO, rowValues: any, entityType: string) => {
  let entityCreator = rowValues['CREATOR_USERID'];
  let entityDepartment = rowValues['DEPARTMENT'];
  let entityResponsible = rowValues['CONTACTS_IDS'].split(',').map(Number);
  if (entityType === et.batchUpdate || entityType === et.batchUpdateSelected)
    return !isCanModifyAsCreator(entityType, user, entityResponsible, entityCreator, entityDepartment);
  else return false;
};

export const getEntityTypeBasedOnHistEntityType = (entityType: string) =>
  et.inventoryHist === entityType
    ? et.inventory
    : et.productHist === entityType
    ? et.product
    : null;

export const isMissingLmsCourse = (user: UserDTO | undefined) => {
  return user && user.missingLmsCourse;
};

export const showEditDeleteButton = (entityType: string, user: UserDTO) => {
  if (isAdmin(user) || isChemicalSafetyExpert(user)) return true;

  return (
    (entityType == et.inventory && isEditor) ||
    (entityType == et.activity && isActivityCanCreate(user, et.activity)) ||
    (entityType == et.product && isProductEditor(user))
  );
};

export const isAllowedToSeeBuildings = (user: UserDTO | undefined) => {
  return (
    isAdmin(user) ||
    isHseDeveloper(user) ||
    isChemicalSafetyExpert(user) ||
    isEnvironmentalExpert(user) ||
    isEditor(user) ||
    isActivityCanCreate(user, 'activity')
  );
};

export default {
  isAdmin,
  isViewer,
  isHseDeveloper,
  isChemicalSafetyExpert,
  isEnvironmentalExpert,
  isFireBrigade,
  isMedicalService,
  isTSO,
  isDSO,
  isPublic,
  isEditor,
  isProductEditor,
  isInventoryOrChemProductCanCreate,
  isCanModifyAsCreator,
  isRowDisabled,
  getEntityTypeBasedOnHistEntityType,
  isInContacts,
  isMissingLmsCourse,
  isGlobalViewer,
  isFGSO,
  isActivityCanCreate,
  isCanViewActivity,
  isAllowedToSeeBuildings,
};
